/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Inference helpers for Pixel Editor extraction rows.
 *
 * These utilities derive `valueType` and `defaultValue` from a
 * {@link MacroExpression} so that the Pixel Editor can automatically
 * populate — and disable — the corresponding UI fields while an
 * expression is active.
 *
 * Both functions are intentionally pure (no side-effects, no React
 * imports) so they can be tested in isolation.
 */

import type { MacroExpression } from '../../../types/decorator-expression';
import type { PixelExtractionRow } from './pixel-editor.adapters';
import { expressionDefault } from './mapping-value-round-trip';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Attempt to detect an appropriate `valueType` from a raw string value.
 *
 * Detection order:
 *  1. `'true'` / `'false'`       → `'boolean'`
 *  2. Integer string (e.g. `'-5'`, `'42'`) → `'integer'`
 *  3. Decimal string (e.g. `'12.50'`) → `'number'`
 *  4. Anything else               → `'string'`
 *
 * Returns `undefined` when `value` is `undefined` or `null` so that
 * callers can distinguish "no hint" from an explicit `'string'`.
 */
export function detectValueTypeFromString(value?: string): PixelExtractionRow['valueType'] {
  if (value === undefined || value === null) return undefined;
  if (value === 'true' || value === 'false') return 'boolean';
  if (/^-?\d+$/.test(value)) return 'integer';
  if (/^-?\d+\.\d+$/.test(value)) return 'number';
  return 'string';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Infer the likely `valueType` of an extraction row from its
 * {@link MacroExpression}, without reading the row's stored `valueType`
 * field.
 *
 * Rules:
 * - **static** – derive from the literal string value via
 *   {@link detectValueTypeFromString}; falls back to `'string'`.
 * - **calculated** – if a `default` string is set, derive from it;
 *   otherwise return `'string'`.
 * - **computed** – arithmetic operations (`add`, `subtract`,
 *   `multiply`, `divide`) → `'number'`; `concat` → `'string'`.
 * - **conditional** – recursively infer from `then`/`else` branches;
 *   when both agree use that type; otherwise prefer `then`.
 * - No expression → `undefined`.
 */
export function inferValueTypeFromExpression(expression?: MacroExpression): PixelExtractionRow['valueType'] {
  if (!expression) return undefined;

  switch (expression.type) {
    case 'static':
      return detectValueTypeFromString(expression.value) ?? 'string';

    case 'calculated': {
      const calculatedDefault = expressionDefault(expression);
      if (calculatedDefault !== undefined && calculatedDefault !== '') {
        const hint = detectValueTypeFromString(calculatedDefault);
        if (hint) return hint;
      }
      return 'string';
    }

    case 'computed': {
      const op = expression.operation ?? 'concat';
      return op === 'concat' ? 'string' : 'number';
    }

    case 'conditional': {
      const thenType = inferValueTypeFromExpression(expression.then);
      const elseType = expression.else ? inferValueTypeFromExpression(expression.else) : undefined;
      if (thenType === elseType) return thenType;
      return thenType ?? elseType ?? 'string';
    }

    default:
      return 'string';
  }
}

/**
 * Derive a sensible `defaultValue` for an extraction row from its
 * {@link MacroExpression}.
 *
 * Rules:
 * - **static** – the expression's own `value` is effectively the default
 *   (it always resolves to that literal); returns `undefined` for the
 *   empty string so the UI shows no value.
 * - **calculated** – the expression's `default` when set.
 * - **computed** – the expression's `default` when set.
 * - **conditional** – recursively extract from the `else` branch (the
 *   branch taken when the condition is false / missing).
 * - No expression, or no extractable default → `undefined`.
 */
export function inferDefaultValueFromExpression(expression?: MacroExpression): PixelExtractionRow['defaultValue'] {
  if (!expression) return undefined;

  switch (expression.type) {
    case 'static':
      return expression.value !== '' ? expression.value : undefined;

    case 'calculated':
      return expressionDefault(expression) || undefined;

    case 'computed':
      return expressionDefault(expression) || undefined;

    case 'conditional':
      if (expression.else) {
        return inferDefaultValueFromExpression(expression.else);
      }
      return undefined;

    default:
      return undefined;
  }
}
